import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import type { OptionFieldDef } from '../../registry/chartTypes';
import type { ChartOptions } from '../../type/dynamicChart';

type OptionFieldRendererProps = {
  field: OptionFieldDef;
  options: ChartOptions;
  onOptionsChange: (updates: Partial<ChartOptions>) => void;
};

function getOptionValue(options: ChartOptions, key: string): unknown {
  return (options as Record<string, unknown>)[key];
}

function setOptionValue(key: string, value: unknown): Partial<ChartOptions> {
  return { [key]: value } as Partial<ChartOptions>;
}

export function OptionFieldRenderer({ field, options, onOptionsChange }: OptionFieldRendererProps) {
  const { t } = useTranslation();

  if (field.kind === 'checkbox') {
    const current = getOptionValue(options, field.key);
    const checked = current !== undefined ? Boolean(current) : field.default;
    return (
      <label className="flex items-center gap-2">
        <Checkbox
          checked={checked}
          onCheckedChange={(value) =>
            onOptionsChange(setOptionValue(field.key, value === true))
          }
        />
        <span className="text-sm">{t(field.labelKey)}</span>
      </label>
    );
  }

  if (field.kind === 'select') {
    const current = getOptionValue(options, field.key);
    const value = current !== undefined ? String(current) : field.default;
    return (
      <div>
        <label className="mb-2 block text-sm font-medium">{t(field.labelKey)}</label>
        <Select
          value={value}
          onValueChange={(v) => {
            // For numeric-looking keys (binCount), parse to number
            const parsed = field.key === 'binCount' ? parseInt(v, 10) : v;
            onOptionsChange(setOptionValue(field.key, parsed));
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.kind === 'number-input') {
    const current = getOptionValue(options, field.key);
    const value = current !== undefined ? current : field.default;
    return (
      <div>
        <label className="mb-2 block text-sm font-medium">{t(field.labelKey)}</label>
        <Input
          type="number"
          value={value === undefined || value === null ? '' : String(value)}
          min={field.min}
          max={field.max}
          onChange={(e) => {
            if (e.target.value === '') {
              onOptionsChange(setOptionValue(field.key, undefined));
            } else {
              const parsed = parseInt(e.target.value, 10);
              onOptionsChange(setOptionValue(field.key, isNaN(parsed) ? undefined : parsed));
            }
          }}
          onBlur={(e) => {
            const parsed = parseInt(e.target.value, 10);
            if (isNaN(parsed)) {
              onOptionsChange(setOptionValue(field.key, field.default));
              return;
            }
            const clamped =
              field.min !== undefined && parsed < field.min
                ? field.min
                : field.max !== undefined && parsed > field.max
                  ? field.max
                  : parsed;
            onOptionsChange(setOptionValue(field.key, clamped));
          }}
          className="w-full"
        />
      </div>
    );
  }

  return null;
}
